import type { CallLogType } from '@/native/simCall'

/** A SIM call the app placed and still waits to find in the call log. */
export interface PendingSimCall {
  callId: string
  /** E.164 */
  number: string
  /** Epoch millis when the app started dialing. */
  startedAt: number
}

export interface LogEntry {
  id: string
  /** E.164, or null when the number could not be read. */
  e164: string | null
  type: CallLogType
  date: number
  durationSec: number
  phoneAccountId?: string
}

export interface CallLogMatch {
  pending: PendingSimCall
  entry: LogEntry
}

/** A call-log row may start up to 10 s before we recorded the dial and up to 3 min after. */
export const MATCH_BEFORE_MS = 10_000
export const MATCH_AFTER_MS = 180_000
/** Pending calls are given up (and left to the server to time out) after a day. */
export const PENDING_TTL_MS = 24 * 60 * 60 * 1000

/**
 * Pairs each pending call with the OUTGOING entry to the same number that started closest to the
 * dial time (inside the window). Each entry is used at most once; oldest pending calls pick first.
 */
export function matchPendingCalls(pending: PendingSimCall[], entries: LogEntry[]): {
  matches: CallLogMatch[]
  unmatched: PendingSimCall[]
} {
  const used = new Set<string>()
  const matches: CallLogMatch[] = []
  const unmatched: PendingSimCall[] = []
  for (const call of [...pending].sort((a, b) => a.startedAt - b.startedAt)) {
    const candidate = entries
      .filter((e) => e.type === 'OUTGOING' && e.e164 === call.number && !used.has(e.id))
      .filter((e) => e.date >= call.startedAt - MATCH_BEFORE_MS && e.date <= call.startedAt + MATCH_AFTER_MS)
      .sort((a, b) => Math.abs(a.date - call.startedAt) - Math.abs(b.date - call.startedAt))[0]
    if (candidate) {
      used.add(candidate.id)
      matches.push({ pending: call, entry: candidate })
    } else {
      unmatched.push(call)
    }
  }
  return { matches, unmatched }
}

/**
 * Other calls worth logging: only with numbers the CRM called from this phone before, so personal
 * calls never leave the device. Entries already matched or uploaded are skipped.
 */
export function crmCallLogExtras(
  entries: LogEntry[],
  crmNumbers: ReadonlySet<string>,
  excludeIds: ReadonlySet<string>
): LogEntry[] {
  return entries.filter(
    (e) =>
      e.e164 !== null &&
      crmNumbers.has(e.e164) &&
      !excludeIds.has(e.id) &&
      (e.type === 'INCOMING' || e.type === 'OUTGOING' || e.type === 'MISSED' || e.type === 'REJECTED')
  )
}

/** Where the next call-log read starts: the oldest pending dial or the last sync, minus a minute. */
export function syncWindowStart(pending: PendingSimCall[], lastSyncAt: number | null, now: number): number {
  const candidates = [lastSyncAt ?? now - PENDING_TTL_MS, ...pending.map((p) => p.startedAt)]
  return Math.min(...candidates) - 60_000
}

/** Pending calls still worth matching. */
export function freshPending(pending: PendingSimCall[], now: number): PendingSimCall[] {
  return pending.filter((p) => now - p.startedAt < PENDING_TTL_MS)
}
